import { graphql } from 'gatsby';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useMemo, useState } from 'react';

import { SEO } from 'components';
import { Container, Section } from 'components/GridV2';
import { BlogArticles } from 'components/pages/coming-soon';
import { Benefits, Features, Hero, Table } from 'components/pages/cover';
import CustomPortableText from 'components/rebrand/CustomPortableText';
import { FAQ, StillHaveQuestions } from 'components/rebrand/FAQ';
import { Layout } from 'components/rebrand/Layout';
import TestimonialsScrolling from 'components/rebrand/TestimonialsScrolling';
import Tiers from 'components/rebrand/Tiers';

import { PageFeatureFlags } from 'models/flags/page';
import { ISanityBlogArticle } from 'typescriptDefs';
import { ICoverOverdraftProtectionPageProps } from 'typescriptDefs/pages/coverOverdraftProtection';

import { afTrackingByPage } from 'helpers/getAfTracking';
import { useIsLDLoaded } from 'helpers/launchDarkly';

const referralCodeMap = {
  'Credit Karma': 'CREDITKARMA50',
};

const CoverPage = ({
  data: { page },
  pageContext: { linkCanonical, locale, pageIsMultilingual, schemaMarkupJson, pageName, variant },
  ...props
}: ICoverOverdraftProtectionPageProps) => {
  const [referralCode, setReferralCode] = useState<string | undefined>(undefined);
  const isSmsAppRegEnabled = useFlags()[PageFeatureFlags.CoverSMSAppRegEnabled];
  const isLDLoaded = useIsLDLoaded();

  useEffect(() => {
    const referralCode = referralCodeMap[variant];
    if (referralCode) {
      localStorage.setItem('kohoReferralCode', referralCode);
      setReferralCode(referralCode);
    }
  }, [variant]);

  const queryParams = afTrackingByPage('COVEROVERDRAFTPROTECTION');
  const queryParamsWithReferral = {
    search: {
      ...queryParams.search,
      af_sub1: referralCode,
    },
    hash: {
      ...queryParams.hash,
      referral: referralCode,
    },
  };

  const sendUserToApp = variant === 'Default' && isLDLoaded && isSmsAppRegEnabled;

  const heroData = {
    heading: page.heroHeading,
    subheading: page.heroSubheading,
    image: page.heroImg,
    imageAltText: page.heroImgAltText,
    btnText: page.btnText_01,
    perks: page.heroPerks,
    tracking: {
      queryParams: queryParamsWithReferral,
      trackEventProps: {
        category: pageName,
        label: 'Accounts Hero',
        locale,
        location: variant,
      },
    },
  };

  const benefitsData = {
    heading: page.benefitsHeading,
    benefits: page.benefits,
    btnText: page.benefitsBtnText,
    image: page.benefitsImg,
    tracking: {
      queryParams: queryParamsWithReferral,
      trackEventProps: {
        category: pageName,
        label: 'Cover Benefits',
        locale,
        location: variant,
      },
    },
  };

  const tableData = {
    headers: page.tableHeaders,
    rows: page.tableRows,
    heading: page.tableHeading,
  };

  const testimonialList = useMemo(
    () => page.testimonials.map((test) => ({ altText: test.altText, img: test.img })),
    [page.testimonials],
  );

  const blogs: ISanityBlogArticle[] = useMemo(
    () =>
      page.blogs.map((blog) => ({
        ...blog,
        img: { url: blog.img.asset.url },
      })),
    [page.blogs],
  );

  return (
    <Layout
      location={props.location}
      pageIsMultilingual={pageIsMultilingual}
      pageName={pageName}
      pageVariant={variant}
      referralCode={referralCode}
      sendUserToAppContext={{
        intent: 'borrowing',
        sendUserToApp,
        oneLink: 'https://koho.onelink.me/Iw0N',
        name: 'cover',
      }}
    >
      <SEO
        canonical={linkCanonical}
        title={page.metadata.title}
        description={page.metadata.description}
        imageSrc={page.metadata.image?.asset?.gatsbyImageData?.images?.fallback?.src}
        keywords={page.metadata.keywords}
        location={props.location}
        multilingual={pageIsMultilingual}
        structuredData={schemaMarkupJson}
      />
      <Hero data={heroData} />
      <Features features={page.features} />
      <Benefits {...benefitsData} />
      <Table data={tableData} />
      <Tiers
        className="py-64 md:py-96"
        heading={page.tiersHeading}
        tiers={page.compareTiers}
        pageName={pageName}
        queryParams={queryParamsWithReferral}
      />
      <TestimonialsScrolling testimonialList={testimonialList} heading={page.testimonialsHeading} disableScroll />
      <Section className="py-64 md:py-96">
        <FAQ
          faqItems={page.faq.faqItems}
          heading={page.faq.heading}
          subheading={page.faq.subheading}
          hideStillHaveQuestions
        />
      </Section>
      <BlogArticles
        heading={page.blogHeading}
        linkText={page.blogLinkText}
        linkUrl={page.blogLinkUrl}
        className="pb-64 pt-0"
        articles={blogs}
      />
      <Container className="px-32 pb-64 pt-32 md:px-[unset] md:pb-96">
        <StillHaveQuestions />
      </Container>

      {page.disclaimers && (
        <Section className="mb-32 md:mb-64">
          <Container className="px-32 xs:px-0">
            <CustomPortableText content={page.disclaimers} />
          </Container>
        </Section>
      )}
    </Layout>
  );
};

export default CoverPage;

export const queryGraphQL = graphql`
  query ($_id: String!) {
    page: sanityPageCoverOverdraftProtection(_id: { eq: $_id }) {
      faq {
        ...sanityFAQ
      }
      metadata {
        ...sanityMetadata
      }
      blogs {
        excerpt: _rawExcerpt
        img {
          asset {
            url
          }
        }
        imgAltText
        minutesToRead
        slug {
          current
        }
        subfolder
        title
      }
      disclaimers: _rawDisclaimers
      heroHeading: _rawV2HeroHeading
      heroSubheading
      btnText_01
      heroPerks {
        icon {
          asset {
            gatsbyImageData(placeholder: NONE, width: 80)
          }
        }
        iconAltText
        description
      }
      heroImg {
        asset {
          gatsbyImageData(placeholder: NONE, width: 1280)
        }
      }
      heroImgAltText
      benefits {
        content
        heading
      }
      benefitsImg {
        imgDesktop {
          asset {
            gatsbyImageData(placeholder: NONE, width: 1076)
          }
        }
        imgMobile {
          asset {
            gatsbyImageData(placeholder: NONE, width: 622)
          }
        }
        altText
      }
      features {
        accordionItems {
          content: _rawContent
          heading
        }
        buttonText
        content: _rawContent
        heading: _rawHeading
        image {
          altText
          img {
            asset {
              gatsbyImageData(placeholder: NONE, width: 1120)
            }
          }
        }
      }
      benefitsBtnText: perksBtnText
      benefitsHeading: _rawPerksHeading
      tiersHeading: _rawTiersHeading
      compareTiers {
        ...sanityTiersCompare
      }
      blogHeading: _rawBlogHeading
      blogLinkUrl
      blogLinkText
      testimonials {
        altText
        img {
          asset {
            gatsbyImageData(placeholder: NONE, width: 648)
          }
        }
      }
      testimonialsHeading: _rawTestimonialsHeading
      tableHeading: _rawTableHeading
      tableHeaders
      tableRows {
        title
        content
      }
    }
  }
`;
